import { dispatch, PassportEvents } from '@lib/events/events'
import { userStore } from '@packages/stores'
import { get } from 'svelte/store'
import type { TLoginOpts, TUser, TUserSocialLink, TGetFriendsPaginatedParams, TGetFriendInfoParams } from '@packages/utils/types'
import { sdk } from '@lib/sdk/sdk'
import {
  getFriendsPaginated,
  sendFriendRequest,
  acceptFriendRequest,
  blockUser,
  unblockUser,
  removeFriend,
  getFriendInfo
} from '@packages/utils/lib/friends'
import { EVM_LOGIN_SCOPE } from '@src/constants/auth-scopes'
import { configStore } from '@src/stores/config'

export async function updateProfile(profileDataOpts: TLoginOpts & TUserSocialLink = {}) {
  try {
    if (!Object.keys(profileDataOpts).length) return

    const updatePromises = []
    if (profileDataOpts.name) updatePromises.push(sdk.account.updateProfile({ name: profileDataOpts.name }))
    if (profileDataOpts.email) updatePromises.push(sdk.account.changeEmail(profileDataOpts.email))
    if (profileDataOpts.phone) updatePromises.push(sdk.account.changePhone(profileDataOpts.phone))

    const res = await Promise.all(updatePromises)
    for (const item of res) {
      if (item.errors?.length) throw new Error(item.errors[0].error)
    }

    const user = await getUser()
    userStore.set(user)
    dispatch(PassportEvents.UPDATE_PROFILE, user)
    return user
  } catch (err) {
    dispatch(PassportEvents.ERROR, { error: err })
    throw err
  }
}

export async function confirmAccount(codes: { phoneCode?: string; emailCode?: string }) {
  try {
    const verifyPromises = []
    if (codes.phoneCode) verifyPromises.push(sdk.account.verifyCode(codes.phoneCode))
    if (codes.emailCode) verifyPromises.push(sdk.account.verifyCode(codes.emailCode))

    await Promise.all(verifyPromises)
    const user = await getUser()
    userStore.set(user)
    dispatch(PassportEvents.ACCOUNT_CONFIRMED, {
      ...(user?.name ? { name: user.name } : {}),
      ...(user?.email ? { email: user.email } : {}),
      ...(user?.phone ? { phone: user.phone } : {}),
    })
    return user
  } catch (err) {
    dispatch(PassportEvents.ERROR, { error: err })
    throw err
  }
}

const getPendingContacts = async () => {
  const pendingUserContracts: { [key: string]: string }[] = await sdk.account.pendingVerificationContacts()

  return pendingUserContracts.reduce((result, item) => {
    if (item.phone) result.phone = item.phone
    if (item.email) result.email = item.email
    return result
  }, {})
}

export const getUser = async (): Promise<TUser> => {
  return {
    ...(await sdk.account.info()),
    pending: {
      ...(await getPendingContacts()),
    },
  }
}


export const deleteAccount = async () => {
  const url = await sdk.oauth.buildLoginRedirectUrl({
    scope: EVM_LOGIN_SCOPE,
    redirectUrl: `${get(configStore)?.credenza?.oauthUiUri}/oauth2/remove`,
  })
  window.open(url.toString(), '_blank');

  const { logout } = await import('./auth')
  await logout({ shouldRevokeSession: true });
}

export const friends = {
  getFriendsPaginated: (params?: TGetFriendsPaginatedParams) => getFriendsPaginated(sdk, params),
  sendFriendRequest: (targetSub: string, clientId?: string) => sendFriendRequest(sdk, targetSub, clientId),
  acceptFriendRequest: (friendshipId: string) => acceptFriendRequest(sdk, friendshipId),
  blockUser: (targetSub: string, clientId?: string) => blockUser(sdk, targetSub, clientId),
  unblockUser: (targetSub: string, clientId?: string) => unblockUser(sdk, targetSub, clientId),
  removeFriend: (targetSub: string, clientId?: string) => removeFriend(sdk, targetSub, clientId),
  getFriendInfo: (targetSub: string, params?: TGetFriendInfoParams) => getFriendInfo(sdk, targetSub, params),
}
